import React from 'react';

import { ReactSerializer } from '@atlaskit/renderer';
import type { ReactSerializerInit } from '@atlaskit/renderer';

import { expVal } from '@confluence/feature-experiments';

import type { NodeTypeCounts } from './NodeTypeCounts';
import { getNodeTypeCounts } from './NodeTypeCounts';

export type SerializeFragmentStackEntry = {
	// This is a list of the nodeTypeCounts of each child node.
	childrenNodeTypeCounts: NodeTypeCounts[];
	// This is a list of whether there are annotations in each child node.
	childrenHasAnnotations: boolean[];
	// This is whether the current node has any annotations on itself. Please note that
	// this does not include annotations on any of its descendants.
	thisHasAnnotations: boolean;
};

// Please note that this is a function instead of a constant because we want a new object each time.
const getEmptySerializeFragmentStackEntry = (): SerializeFragmentStackEntry => ({
	childrenNodeTypeCounts: [],
	childrenHasAnnotations: [],
	thisHasAnnotations: false,
});

export class VirtualizedReactSerializer extends ReactSerializer {
	// This stack is used to keep track information on nodes as they are recursively
	// serialized depth first. The last index is the top of the stack.
	private vrs_serializeFragmentStack: SerializeFragmentStackEntry[];
	private vrs_isVirtualizedTablesEnabled: boolean;

	constructor(init: ReactSerializerInit) {
		super(init);
		this.vrs_serializeFragmentStack = [];
		this.vrs_isVirtualizedTablesEnabled = expVal(
			'confluence_frontend_virtualized_tables',
			'isEnabled',
			false,
		);

		if (this.vrs_isVirtualizedTablesEnabled) {
			this.overrideSerializeMark();
		}
	}

	/**
	 * Like {@link ReactSerializer.resetState}. Named differently because a class cannot have a separate declaration of a private property of its base class.
	 */
	private vrs_resetVirtualizedState() {
		this.vrs_serializeFragmentStack = [];
	}

	/**
	 * @override
	 */
	serializeFragment(
		_fragment: SerializeFragmentParameters[0],
		props?: SerializeFragmentParameters[1],
		_target?: SerializeFragmentParameters[2],
		key?: SerializeFragmentParameters[3],
		parentInfo?: SerializeFragmentParameters[4],
	): JSX.Element | null {
		if (!this.vrs_isVirtualizedTablesEnabled) {
			return super.serializeFragment.apply(
				this,
				arguments as unknown as SerializeFragmentParameters,
			);
		}

		const isRoot = key === undefined; // Like ReactSerializer.serializeFragment
		if (isRoot) {
			this.vrs_resetVirtualizedState();
		}

		this.vrs_preSerializeFragment();
		const nodeElement = super.serializeFragment.apply(
			this,
			arguments as unknown as SerializeFragmentParameters,
		);
		const {
			selfNodeTypeCounts,
			selfHasAnnotations,
			childrenNodeTypeCounts,
			childrenHasAnnotations,
		} = this.vrs_postSerializeFragment(parentInfo);

		if (nodeElement && props?.nodeType === 'table') {
			return React.cloneElement(nodeElement, {
				parentInfo,
				childInfo: {
					selfNodeTypeCounts,
					selfHasAnnotations,
					childrenNodeTypeCounts,
					childrenHasAnnotations,
				},
			});
		}

		return nodeElement;
	}

	private vrs_preSerializeFragment = () => {
		// Adds a new object onto childInfoOutputStack for this node. This allows children to report their
		// estimated heights when they are serialized.
		this.vrs_serializeFragmentStack.push(getEmptySerializeFragmentStackEntry());
	};

	private vrs_postSerializeFragment = (parentInfo: SerializeFragmentParameters[4]) => {
		// Removes the new object from childInfoOutputStack for this node.
		const thisStack =
			this.vrs_serializeFragmentStack.pop() || getEmptySerializeFragmentStackEntry();

		// Get the node type counts for this node based on its type and the counts of its children.
		const thisNode = parentInfo?.path?.[parentInfo.path.length - 1];
		const selfNodeTypeCounts = thisNode
			? getNodeTypeCounts(thisNode, thisStack.childrenNodeTypeCounts)
			: {};
		const selfHasAnnotations =
			thisStack.thisHasAnnotations || thisStack.childrenHasAnnotations.some((item) => item);

		// Reports the counts for the node types of this node and its children to its parent node.
		if (this.vrs_serializeFragmentStack.length > 0) {
			// Pushes the aggregated NodeTypeCounts for the parent node to use.
			this.vrs_serializeFragmentStack[
				this.vrs_serializeFragmentStack.length - 1
			].childrenNodeTypeCounts.push(selfNodeTypeCounts);
			this.vrs_serializeFragmentStack[
				this.vrs_serializeFragmentStack.length - 1
			].childrenHasAnnotations.push(selfHasAnnotations);
		}

		return {
			selfNodeTypeCounts,
			selfHasAnnotations,
			childrenNodeTypeCounts: thisStack.childrenNodeTypeCounts,
			childrenHasAnnotations: thisStack.childrenHasAnnotations,
		};
	};

	private overrideSerializeMark() {
		// `serializeMark` is private to `ReactSerializer`, but it's more efficient for this experiment to not go through the trouble of aligning with platform and making it protected.
		// If the experiment turns out to be successful, then the reliability and maintainability of this solution will be improved by making `serializeMark` protected in `ReactSerializer`.
		const superSerializeMark = this[SERIALIZE_MARK_PNAME];
		if (typeof superSerializeMark === 'function') {
			this[SERIALIZE_MARK_PNAME] = function serializeMark({ mark }) {
				const hasAnnotation = (mark?.['marks'] || []).some(
					(m: { type: { name: string } }) => m.type.name === 'annotation',
				);
				if (hasAnnotation) {
					// We update the current node on the stack to indicate that the current node being
					// serialized has an annotation. Because serializeFragment is recursive, this means
					// we are updating the lowest level element currently being serialized. It is also
					// worth noting that text nodes are not handled by serializeFragment, so will not
					// appear in the stack. Instead, their parent node will be updated. In many, but not
					// all cases, this will be a paragraph node.
					this.vrs_serializeFragmentStack[
						this.vrs_serializeFragmentStack.length - 1
					].thisHasAnnotations = true;
				}

				return superSerializeMark.apply(this, arguments as unknown as SerializeMarkParameters);
			};
		}
	}
}

/**
 * The name of the {@link ReactSerializer} method/property which serializes marks.
 */
const SERIALIZE_MARK_PNAME = 'serializeMark';
type SerializeMarkParameters = Parameters<ReactSerializer[typeof SERIALIZE_MARK_PNAME]>;

type SerializeFragmentParameters = Parameters<ReactSerializer['serializeFragment']>;
